import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { formatWireForDisplay, type WireMoney } from '../money.js'
import { DataSurface } from './DataSurface.js'

/**
 * `PRC-012`: what a draft price book would have done to the last 90 days of lens sales.
 *
 * **A read, so it does not go through the write queue.** `GET /price-books/versions/:id/simulation`
 * writes nothing and takes no idempotency key. Publishing is a different screen and a different
 * permission, and nothing here is a precondition of it (`V1-GAP-PLANS.md`: a simulation gate would
 * be a gate the spec does not state).
 *
 * **Every figure is the server's.** Money arrives as `{ minor, currency }` strings and is shown by
 * `../money.ts`. There is no sum in this file: a total computed here would be a number the engine
 * never produced, and `MR-04` spends its rounding in the engine.
 *
 * **The draft is named by its id.** There is no endpoint that lists draft versions yet, so the
 * field takes the id the price book tooling shows. Said here because a picker is the obvious
 * improvement and it needs that list first.
 */

export interface PriceImpactProduct {
  readonly productId: string
  readonly name: string
  readonly lineCount: number
  readonly baseline: WireMoney
  readonly candidate: WireMoney
  readonly delta: WireMoney
  readonly minDelta: WireMoney
  readonly maxDelta: WireMoney
}

export interface PriceImpactProblem {
  readonly productId: string
  /** Which book could not price the line. Absent for a line that was left out before pricing. */
  readonly side?: 'baseline' | 'candidate'
  readonly code: string
  readonly message: string
}

export interface PriceImpactData {
  readonly windowDays: number
  readonly lineCap: number
  readonly truncated: boolean
  readonly totals: {
    readonly lineCount: number
    readonly baseline: WireMoney
    readonly candidate: WireMoney
    readonly delta: WireMoney
    readonly storedPriced: WireMoney
  }
  readonly products: readonly PriceImpactProduct[]
  readonly errors: readonly PriceImpactProblem[]
  readonly skipped: readonly PriceImpactProblem[]
}

export interface PriceImpactProps {
  readonly load: (versionId: string) => Promise<PriceImpactData>
  /** `PRC-013`: the shop's own rounding rule. Null means it has not set one. */
  readonly roundingSet: boolean
}

const show = (amount: WireMoney): string => formatWireForDisplay(amount.minor, amount.currency)

/** The same sentence on many lines is one row with a count, not a wall. */
function grouped(
  problems: readonly PriceImpactProblem[],
): readonly { key: string; count: number; problem: PriceImpactProblem }[] {
  const byKey = new Map<string, { key: string; count: number; problem: PriceImpactProblem }>()
  for (const problem of problems) {
    const key = `${problem.side ?? ''}|${problem.code}|${problem.message}`
    const seen = byKey.get(key)
    if (seen) seen.count += 1
    else byKey.set(key, { key, count: 1, problem })
  }
  return [...byKey.values()]
}

export function PriceImpact({ load, roundingSet }: PriceImpactProps): JSX.Element {
  const { t } = useTranslation()
  const [versionId, setVersionId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<PriceImpactData | null>(null)

  const preview = useCallback(async () => {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult(await load(versionId.trim()))
    } catch (loadError) {
      // The server's own sentence: it says whether the version is not a draft or does not exist.
      setError(loadError instanceof Error ? loadError.message : t('priceImpact.loadFailed'))
    } finally {
      setBusy(false)
    }
  }, [load, versionId, t])

  return (
    <section className="lm-screen" aria-labelledby="price-impact-heading">
      <h1 id="price-impact-heading" className="lm-screen__heading">
        {t('priceImpact.heading')}
      </h1>
      <p className="lm-hint">{t('priceImpact.intro', { days: 90 })}</p>

      {roundingSet ? null : (
        <p className="lm-hint" role="alert">
          {t('priceImpact.noRounding')}
        </p>
      )}

      <div className="lm-form">
        <label className="lm-field">
          <span className="lm-field__label">{t('priceImpact.versionLabel')}</span>
          <input
            className="lm-input"
            type="text"
            dir="ltr"
            value={versionId}
            onChange={(e) => {
              setVersionId(e.currentTarget.value)
            }}
          />
        </label>
        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={busy || !roundingSet || versionId.trim() === ''}
          onClick={() => void preview()}
        >
          {t('priceImpact.preview')}
        </button>
      </div>

      {/*
        §8 `ST-02`, for the two states a shopkeeper hits here: pricing in progress and a refused or
        failed preview. There is no idle state, so nothing is drawn before the first press. The
        server's own sentence is kept as the error because it says whether the version is not a
        draft or does not exist; retry is offered anyway for the network case.
      */}
      {busy || error !== null ? (
        <DataSurface
          state={
            busy
              ? { kind: 'loading' }
              : { kind: 'error', message: error ?? '', onRetry: () => void preview() }
          }
          shape="table"
          rows={4}
          columns={7}
          what={t('priceImpact.what')}
        >
          {null}
        </DataSurface>
      ) : null}

      {result === null ? null : (
        <>
          {result.truncated ? (
            <p className="lm-hint" role="alert">
              {t('priceImpact.truncated', { count: result.lineCap })}
            </p>
          ) : null}

          {/*
            One empty sentence and no first-run or filtered variant: it means no comparable lens
            was sold in the window, which is the same situation on day one and on day 300.
          */}
          <DataSurface
            state={result.totals.lineCount === 0 ? { kind: 'empty' } : { kind: 'ready' }}
            shape="table"
            rows={4}
            columns={7}
            what={t('priceImpact.what')}
            emptyMessage={t('priceImpact.nothing', { days: result.windowDays })}
          >
            <p className="lm-hint">
              {t('priceImpact.compared', { count: result.totals.lineCount })}
            </p>
            <table className="lm-line-table">
              <caption className="lm-rx-grid__caption">{t('priceImpact.totalsCaption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('priceImpact.product')}</th>
                  <th scope="col" className="lm-line-table__num">
                    {t('priceImpact.lines')}
                  </th>
                  <th scope="col" className="lm-line-table__num">
                    {t('priceImpact.before')}
                  </th>
                  <th scope="col" className="lm-line-table__num">
                    {t('priceImpact.after')}
                  </th>
                  <th scope="col" className="lm-line-table__num">
                    {t('priceImpact.change')}
                  </th>
                  <th scope="col" className="lm-line-table__num">
                    {t('priceImpact.smallest')}
                  </th>
                  <th scope="col" className="lm-line-table__num">
                    {t('priceImpact.largest')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.products.map((product) => (
                  <tr key={product.productId}>
                    <td>{product.name}</td>
                    <td className="lm-line-table__num" dir="ltr">
                      {product.lineCount}
                    </td>
                    <td className="lm-line-table__num" dir="ltr">
                      {show(product.baseline)}
                    </td>
                    <td className="lm-line-table__num" dir="ltr">
                      {show(product.candidate)}
                    </td>
                    <td className="lm-line-table__num" dir="ltr">
                      {show(product.delta)}
                    </td>
                    <td className="lm-line-table__num" dir="ltr">
                      {show(product.minDelta)}
                    </td>
                    <td className="lm-line-table__num" dir="ltr">
                      {show(product.maxDelta)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">{t('priceImpact.total')}</th>
                  <td className="lm-line-table__num" dir="ltr">
                    {result.totals.lineCount}
                  </td>
                  <td className="lm-line-table__num" dir="ltr">
                    {show(result.totals.baseline)}
                  </td>
                  <td className="lm-line-table__num" dir="ltr">
                    {show(result.totals.candidate)}
                  </td>
                  <td className="lm-line-table__num" dir="ltr">
                    {show(result.totals.delta)}
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
            <p className="lm-hint">
              {t('priceImpact.charged')} <Ltr>{show(result.totals.storedPriced)}</Ltr>
            </p>
          </DataSurface>

          {result.errors.length === 0 ? null : (
            <>
              <h2 className="lm-screen__subheading">{t('priceImpact.errorsHeading')}</h2>
              <ul>
                {grouped(result.errors).map(({ key, count, problem }) => (
                  <li key={key}>
                    {t('priceImpact.problem', {
                      count,
                      side:
                        problem.side === 'baseline'
                          ? t('priceImpact.side.baseline')
                          : t('priceImpact.side.candidate'),
                      message: problem.message,
                    })}
                  </li>
                ))}
              </ul>
            </>
          )}

          {result.skipped.length === 0 ? null : (
            <>
              <h2 className="lm-screen__subheading">{t('priceImpact.skippedHeading')}</h2>
              <ul>
                {grouped(result.skipped).map(({ key, count, problem }) => (
                  <li key={key}>{t('priceImpact.skipped', { count, message: problem.message })}</li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  )
}
